import { useCallback, useMemo } from 'react';

import { HandlerBuilder, LegacyTopic, WalletCommandTopic, WalletConnectCommandPayload } from '@coinweb/webapp-library';

import type { BaseHandlers } from './useWebappCommandLifecycleCore';
import { useWebappCommandLifecycleCore } from './useWebappCommandLifecycleCore';

type LegacyDataHandler = (payload: { user_pubkey?: string }) => void;

interface ConnectHandlers extends BaseHandlers {
  onLegacyData: LegacyDataHandler;
}

export const useWalletConnectCore = () => {
  const config = useMemo(
    () => ({
      topic: WalletCommandTopic.Connect as typeof WalletCommandTopic.Connect,
      buildInitialRequest: (correlationId: string, payload: WalletConnectCommandPayload) => ({
        id: correlationId,
        topic: WalletCommandTopic.Connect as typeof WalletCommandTopic.Connect,
        payload,
      }),
      setupSubscription: (builder: HandlerBuilder, handlers: ConnectHandlers) => {
        // Add legacy data handler for backward compatibility
        builder.handle(LegacyTopic.Data, (payload: unknown) => {
          handlers.onLegacyData(payload as { user_pubkey?: string });
        });
      },
    }),
    []
  );

  const {
    executeCommand: executeCore,
    cancelPendingRequest,
    isExecuting: isConnecting,
  } = useWebappCommandLifecycleCore<WalletConnectCommandPayload, WalletCommandTopic.Connect, ConnectHandlers>(config);

  const connect = useCallback(
    (handlers: ConnectHandlers): string | null => {
      const connectWalletPayload = {
        appInfo: {
          name: 'PACT SWAP',
          description: 'A cross-chain DEX for native $BTC, $ETH, and more',
          icon: 'https://app.pactswap.io/favicon.svg',
          url: window.location.origin,
        },
        redirectUrl: import.meta.env.VITE_REDIRECT_URL,
      };

      return executeCore(connectWalletPayload, handlers);
    },
    [executeCore]
  );

  const disconnect = useCallback(() => {
    void cancelPendingRequest();
  }, [cancelPendingRequest]);

  return {
    // actions
    connect,
    cancelPendingRequest,
    disconnect,
    // state
    isConnecting,
  };
};
